import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { UnifiedCard } from "@/components/cards/UnifiedCard";
import { Badge } from "@/components/ui/badge";
import { Swords, Play, Users, UserPlus, Clock, Loader2, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AdventureCardActions } from "@/components/cards/AdventureCardActions";
import { CreditBadge } from "@/components/credits/CreditBadge";
import { useLocale } from "@/hooks/useLocale";
import { fmtDate } from "@/lib/locale-format";
import { cacheAdventure } from "@/lib/adventure-cache";
import type { Translations } from "@/lib/i18n/types";
import type { JoinedAdventureItem } from "@/hooks/useIndexData";

function formatTimeAgo(dateStr: string, locale: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDate(new Date(dateStr), locale);
}

interface PlayTabProps {
  t: Translations;
  adventures: any[];
  joinedAdventures: JoinedAdventureItem[];
  worlds: any[];
  sharedWorlds: any[];
  adventureWorlds: any[];
  playableWorlds: any[];
  showNewGame: boolean;
  setShowNewGame: (v: boolean) => void;
  selectedWorldId: string;
  setSelectedWorldId: (v: string) => void;
  adventureName: string;
  setAdventureName: (v: string) => void;
  isMultiplayer: boolean;
  setIsMultiplayer: (v: boolean) => void;
  turnMode: string;
  setTurnMode: (v: string) => void;
  creatingGame: boolean;
  setCreatingGame: (v: boolean) => void;
  handleStartGame: () => void;
  showJoin: boolean;
  setShowJoin: (v: boolean) => void;
  joinCode: string;
  setJoinCode: (v: string) => void;
  joining: boolean;
  handleJoinGame: () => void;
  copiedId: string | null;
  copyInviteLink: (code: string, id: string) => void;
  navigate: (path: string) => void;
  currentUserId: string | null;
  handleDeleteAdventure: (id: string) => void;
  handleLeaveAdventure: (id: string) => void;
  adventureCredits: Record<string, number>;
}

export function PlayTab({
  t, adventures, joinedAdventures, worlds, sharedWorlds, adventureWorlds,
  playableWorlds,
  showNewGame, setShowNewGame, selectedWorldId, setSelectedWorldId,
  adventureName, setAdventureName, isMultiplayer, setIsMultiplayer,
  turnMode, setTurnMode, creatingGame, setCreatingGame, handleStartGame,
  showJoin, setShowJoin, joinCode, setJoinCode, joining, handleJoinGame,
  copiedId, copyInviteLink, navigate, currentUserId,
  handleDeleteAdventure, handleLeaveAdventure, adventureCredits,
}: PlayTabProps) {
  const { locale } = useLocale();
  const ownAdventures = (adventures || []).map((adv: any) => ({ ...adv, _source: "own" }));
  const joinedMapped = (joinedAdventures || []).map((j: JoinedAdventureItem) => ({
    ...j.adventure,
    _source: "joined",
    _playerId: j.player_id,
    _isMyTurn: j.is_my_turn,
    _characterName: j.character_name,
  }));

  const allAdventures = [...ownAdventures, ...joinedMapped].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  const yourTurn = allAdventures.filter((a) => a._isMyTurn);
  const rest = allAdventures.filter((a) => !a._isMyTurn);

  const resolveWorld = (worldId: string) => {
    const own = worlds.find((w: any) => w.id === worldId);
    if (own) return { name: own.name, icon: own.icon || "🌍", banner_url: own.banner_url, description: own.description };
    const shared = sharedWorlds.find((w: any) => w.id === worldId);
    if (shared) return { name: shared.name, icon: shared.icon || "🌍", banner_url: shared.banner_url, description: shared.description };
    const advWorld = adventureWorlds.find((w: any) => w.id === worldId);
    if (advWorld) return { name: advWorld.name, icon: advWorld.icon || "🌍", banner_url: advWorld.banner_url, description: advWorld.description };
    return { name: "Unknown World", icon: "🌍", banner_url: null, description: null };
  };

  const renderAdventureCard = (adv: any) => {
    const world = resolveWorld(adv.world_id);
    const isYourTurn = adv._isMyTurn;
    const turnCount = (adv.state as any)?.turn_count || 0;
    const pc = adv.player_character || adv._playerCharacter;
    const charName = adv._characterName || pc?.name || null;
    const charImageUrl = pc?.image_url || null;
    const playerCount = adv._player_count || 1;
    const currentLocation = (adv.state as any)?.current_location;
    const scenarioDesc = (adv.state as any)?.scenario_description || (adv.state as any)?.opening_narration;
    const advDescription = currentLocation
      ? `Currently at ${currentLocation}.${scenarioDesc ? ` ${scenarioDesc}` : (world.description ? ` ${world.description}` : "")}`
      : scenarioDesc || world.description || null;

    return (
      <UnifiedCard
        key={adv.id}
        id={adv.id}
        cardType="adventure"
        onClick={() => { cacheAdventure(adv); navigate(`/play/${adv.id}`); }}
        bannerUrl={world.banner_url}
        icon={null}
        iconFallback="⚔️"
        title={adv.name}
        description={advDescription}
        highlighted={isYourTurn}
        accentBar={isYourTurn}
        badges={
          <>
            {isYourTurn && (
              <Badge className="text-[10px] gap-1 bg-primary text-primary-foreground animate-pulse">
                <Swords className="w-3 h-3" /> {t.idx_your_turn}
              </Badge>
            )}
            {adv.is_multiplayer ? (
              <Badge variant="secondary" className="text-[10px] gap-1 bg-background/80 backdrop-blur-sm">
                <Users className="w-3 h-3" /> {playerCount}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] bg-background/80 backdrop-blur-sm border-border/50">
                {t.idx_solo}
              </Badge>
            )}
            {adv._source === "joined" && (
              <Badge variant="outline" className="text-[10px] bg-background/80 backdrop-blur-sm border-border/50">{t.idx_joined}</Badge>
            )}
          </>
        }
        extraContent={
          <div data-slot="world-info" className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              {world.icon && (world.icon.startsWith?.("http") || world.icon.startsWith?.("data:"))
                ? <img src={world.icon} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" loading="lazy" decoding="async" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                : <span className="text-2xl shrink-0">{world.icon}</span>
              }
              <span className="truncate font-medium">{world.name}</span>
            </div>
            {charName && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <div data-slot="character" className="flex items-center gap-1.5 text-xs text-foreground/80 min-w-0">
                  {charImageUrl ? (
                    <img src={charImageUrl} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" loading="lazy" decoding="async" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  ) : (
                    <User className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                  <span className="truncate font-medium">{charName}</span>
                </div>
              </>
            )}
          </div>
        }
        footerActions={
          <AdventureCardActions
            adventureId={adv.id}
            adventureName={adv.name}
            isOwner={adv._source === "own"}
            isMultiplayer={adv.is_multiplayer}
            inviteCode={adv.invite_code}
            onContinue={() => { cacheAdventure(adv); navigate(`/play/${adv.id}`); }}
            onInvite={adv.is_multiplayer && adv.invite_code ? () => copyInviteLink(adv.invite_code!, adv.id) : undefined}
            onLeave={adv._source === "joined" ? () => handleLeaveAdventure(adv.id) : undefined}
            onDelete={adv._source === "own" ? () => handleDeleteAdventure(adv.id) : undefined}
          />
        }
        footer={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] gap-1 font-mono">
              {t.idx_turn} {turnCount}
            </Badge>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock className="w-3 h-3" />
              {formatTimeAgo(adv.updated_at, locale)}
            </span>
            {(adventureCredits[adv.id] || 0) > 0 && <CreditBadge amount={adventureCredits[adv.id]} />}
          </div>
        }
      />
    );
  };

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap gap-3">
        <Button className="gap-2" onClick={() => setShowNewGame(true)}>
          <Swords className="w-4 h-4" />{t.idx_new_game}
        </Button>
        <Dialog open={showJoin} onOpenChange={setShowJoin}>
          <DialogTrigger asChild>
            <Button variant="outline" className="gap-2"><UserPlus className="w-4 h-4" />{t.idx_join_game}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t.idx_join_game_title}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">{t.idx_join_game_desc}</p>
            <div className="flex gap-2">
              <Input placeholder={t.idx_invite_code_placeholder} value={joinCode}
                onChange={(e: any) => setJoinCode(e.target.value)}
                onKeyDown={(e: any) => e.key === "Enter" && handleJoinGame()} className="flex-1" />
              <Button onClick={handleJoinGame} disabled={!joinCode.trim() || joining} className="gap-1">
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                {t.idx_join}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {yourTurn.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Swords className="w-5 h-5 text-primary animate-pulse" />
            {t.idx_your_turn}
            <Badge className="bg-primary text-primary-foreground">{yourTurn.length}</Badge>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {yourTurn.map(renderAdventureCard)}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Play className="w-5 h-5 text-secondary" />
          {t.idx_adventures}
          <Badge variant="secondary">{rest.length}</Badge>
        </h2>
        {rest.length === 0 && yourTurn.length === 0 ? (
          <Card className="p-12 text-center space-y-4">
            <Swords className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <p className="text-muted-foreground">{t.idx_no_adventures_hint}</p>
          </Card>
        ) : rest.length === 0 ? null : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rest.map(renderAdventureCard)}
          </div>
        )}
      </section>
    </div>
  );
}
